import { useRecordContext, useGetList, Loading, useTranslate } from 'react-admin';
import { useDateFormat } from '../../../utils/dateFormat';
import {
  Box,
  Typography,
  List as MuiList,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import { useNavigate } from 'react-router-dom';
import { ChannelTypeIcon } from './ChannelTypeIcon';
import type { Contact, Conversation } from '../../../types/chat.types';

/**
 * Displays the list of conversations associated with a contact.
 * Conversations that have an open ticket navigate to the tickets page.
 * Conversations without a ticket show a non-clickable informational row.
 */
export const ContactConversations = () => {
  const record = useRecordContext<Contact>();
  const translate = useTranslate();
  const navigate = useNavigate();
  const { formatDateTime } = useDateFormat();

  const { data: conversations, isLoading } = useGetList<Conversation>(
    'conversation',
    {
      filter: { contactId_eq: record?.id },
      pagination: { page: 1, perPage: 20 },
      sort: { field: 'lastActivityAt', order: 'DESC' },
    },
    { enabled: !!record?.id }
  );

  if (isLoading) return <Loading />;
  if (!conversations?.length) {
    return (
      <Typography variant="body2" color="textSecondary">
        {translate('resources.contact.conversations.empty')}
      </Typography>
    );
  }

  const handleClick = (conversation: Conversation) => {
    const ticket = conversation.openTicket || conversation.latestTicket;
    if (ticket) {
      navigate(`/tickets?ticket=${ticket.id}`);
    }
  };

  return (
    <MuiList dense disablePadding>
      {conversations.map((conversation) => {
        const ticket = conversation.openTicket || conversation.latestTicket;
        const channelType = conversation.channelConnection?.channel?.type;

        return (
          <ListItem
            key={conversation.id}
            onClick={ticket ? () => handleClick(conversation) : undefined}
            sx={{
              cursor: ticket ? 'pointer' : 'default',
              borderRadius: 1,
              mb: 0.5,
              '&:hover': ticket ? { bgcolor: 'action.hover' } : undefined,
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              {channelType ? (
                <ChannelTypeIcon type={channelType} />
              ) : (
                <ChatIcon color="action" />
              )}
            </ListItemIcon>
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2" fontWeight={500}>
                    {conversation.name || conversation.contact?.name || `${translate('resources.contact.conversations.conversation')} #${conversation.id}`}
                  </Typography>
                  {ticket && (
                    <Chip
                      icon={<ConfirmationNumberIcon sx={{ fontSize: 14 }} />}
                      label={`#${ticket.id}`}
                      size="small"
                      color={conversation.openTicket ? 'primary' : 'default'}
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
              }
              secondary={
                <Box component="span" display="flex" flexDirection="column" gap={0.25} mt={0.25}>
                  {conversation.lastMessage && (
                    <Typography variant="caption" color="textSecondary" component="span" noWrap>
                      {conversation.lastMessage.length > 80
                        ? conversation.lastMessage.substring(0, 80) + '...'
                        : conversation.lastMessage}
                    </Typography>
                  )}
                  {conversation.lastActivityAt && (
                    <Typography variant="caption" color="textSecondary" component="span">
                      {formatDateTime(conversation.lastActivityAt)}
                    </Typography>
                  )}
                </Box>
              }
            />
          </ListItem>
        );
      })}
    </MuiList>
  );
};
